import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { type Studio, type StudioMember, StudioSchema, StudioMemberSchema } from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AuthContextValue = {
  status: 'loading' | 'authenticated' | 'unauthenticated';
  session: Session | null;
  user: User | null;
  memberships: StudioMember[];
  studios: Studio[];
  activeStudioId: string | null;
  setActiveStudioId: (id: string) => void;
  refreshMemberships: () => Promise<void>;
  signOut: () => Promise<void>;
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers: snake_case DB row -> camelCase Zod shape
// ---------------------------------------------------------------------------

type RawStudioRow = {
  id: unknown;
  name: unknown;
  slug: unknown;
  short_id: unknown;
  team_size: unknown;
  use_case: unknown;
  stripe_customer_id: unknown;
  subscription_status: unknown;
  created_at: unknown;
  updated_at: unknown;
};

type RawMemberRow = {
  id: unknown;
  studio_id: unknown;
  user_id: unknown;
  role: unknown;
  invited_at: unknown;
  joined_at: unknown;
  created_at: unknown;
  updated_at: unknown;
  studio: RawStudioRow | null;
};

function mapStudioRow(raw: RawStudioRow): Record<string, unknown> {
  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    shortId: raw.short_id,
    teamSize: raw.team_size,
    useCase: raw.use_case,
    stripeCustomerId: raw.stripe_customer_id,
    subscriptionStatus: raw.subscription_status,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

function mapMemberRow(raw: RawMemberRow): Record<string, unknown> {
  return {
    id: raw.id,
    studioId: raw.studio_id,
    userId: raw.user_id,
    role: raw.role,
    invitedAt: raw.invited_at,
    joinedAt: raw.joined_at,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

const ACTIVE_STUDIO_KEY = 'playerbase.activeStudioId';

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<StudioMember[]>([]);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [activeStudioId, setActiveStudioIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_STUDIO_KEY);
    } catch {
      return null;
    }
  });
  // 'loading' until getSession + (if authenticated) first memberships load resolves
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  // Tracks whether we have resolved the initial session check
  const initialised = useRef(false);

  const loadMemberships = useCallback(async (userId: string, signal?: { aborted: boolean }): Promise<void> => {
    const { data, error } = await supabase
      .from('studio_members')
      .select('*, studio:studios(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (signal?.aborted) return;

    if (error) {
      console.warn('[AuthProvider] Failed to load memberships:', error.message);
      setMemberships([]);
      setStudios([]);
      return;
    }

    const parsedMembers: StudioMember[] = [];
    const parsedStudios: Studio[] = [];

    for (const raw of (data ?? []) as RawMemberRow[]) {
      const memberResult = StudioMemberSchema.safeParse(mapMemberRow(raw));
      const studioResult = raw.studio !== null
        ? StudioSchema.safeParse(mapStudioRow(raw.studio))
        : null;

      if (!memberResult.success) {
        console.warn('[AuthProvider] Could not parse studio_member row; skipping.', memberResult.error.issues);
        continue;
      }
      if (studioResult !== null && !studioResult.success) {
        console.warn('[AuthProvider] Could not parse studio row; skipping.', studioResult.error.issues);
        continue;
      }

      parsedMembers.push(memberResult.data);
      if (studioResult?.success) {
        parsedStudios.push(studioResult.data);
      }
    }

    if (signal?.aborted) return;

    setMemberships(parsedMembers);
    setStudios(parsedStudios);

    // Reconcile activeStudioId: if persisted value is not in the new list, reset
    setActiveStudioIdState((prev) => {
      const ids = parsedMembers.map((m) => m.studioId);
      if (prev !== null && ids.includes(prev)) return prev;
      const fallback = parsedMembers[0]?.studioId ?? null;
      try {
        if (fallback !== null) {
          localStorage.setItem(ACTIVE_STUDIO_KEY, fallback);
        } else {
          localStorage.removeItem(ACTIVE_STUDIO_KEY);
        }
      } catch {
        // localStorage may be unavailable
      }
      return fallback;
    });
  }, []);

  const setActiveStudioId = useCallback((id: string): void => {
    setActiveStudioIdState(id);
    try {
      localStorage.setItem(ACTIVE_STUDIO_KEY, id);
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await supabase.auth.signOut();
    // onAuthStateChange will clear session/user/memberships
  }, []);

  const refreshMemberships = useCallback(async (): Promise<void> => {
    if (user === null) return;
    await loadMemberships(user.id);
  }, [user, loadMemberships]);

  useEffect(() => {
    const abortSignal = { aborted: false };

    // 1. Get the initial session
    void supabase.auth.getSession().then(async ({ data: { session: initial } }) => {
      if (abortSignal.aborted) return;

      if (initial?.user) {
        // Validate the JWT against the server. getSession() only decodes the
        // token from localStorage, so after a `supabase db reset` (or any
        // server-side user deletion) the browser still thinks it's signed in.
        // Round-trip via getUser() to detect a stale token and clear it
        // instead of stranding the user on /welcome with no escape.
        const { data: { user: live }, error } = await supabase.auth.getUser();
        if (abortSignal.aborted) return;

        if (error !== null || live === null) {
          await supabase.auth.signOut();
          if (abortSignal.aborted) return;
          setSession(null);
          setUser(null);
          initialised.current = true;
          setStatus('unauthenticated');
          return;
        }

        setSession(initial);
        setUser(live);
        void loadMemberships(live.id, abortSignal).then(() => {
          if (abortSignal.aborted) return;
          initialised.current = true;
          setStatus('authenticated');
        });
      } else {
        setSession(null);
        setUser(null);
        initialised.current = true;
        setStatus('unauthenticated');
      }
    });

    // 2. Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (abortSignal.aborted) return;

      // Push the latest access token into the Realtime client whenever
      // the session rotates. supabase-js v2 propagates the token to new
      // channels automatically, but already-subscribed channels keep
      // their original JWT until we force-refresh - which is what
      // makes long-lived inbox tabs go silently stale around the
      // hourly token-refresh boundary (channel stays SUBSCRIBED, but
      // the worker-side RLS check starts rejecting events because the
      // JWT it has on file has expired). setAuth is idempotent and
      // safe to call on every state change including SIGNED_OUT
      // (which clears the token).
      void supabase.realtime.setAuth(newSession?.access_token ?? null);

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        // After initial boot, auth state changes resolve immediately without
        // re-entering 'loading' - memberships load in the background.
        void loadMemberships(newSession.user.id, abortSignal).then(() => {
          if (abortSignal.aborted) return;
          setStatus('authenticated');
        });
      } else {
        // Signed out: clear memberships and mark unauthenticated
        setMemberships([]);
        setStudios([]);
        setActiveStudioIdState(null);
        try {
          localStorage.removeItem(ACTIVE_STUDIO_KEY);
        } catch {
          // localStorage may be unavailable
        }
        if (initialised.current) {
          setStatus('unauthenticated');
        }
      }
    });

    return () => {
      abortSignal.aborted = true;
      subscription.unsubscribe();
    };
  }, [loadMemberships]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      user,
      memberships,
      studios,
      activeStudioId,
      setActiveStudioId,
      refreshMemberships,
      signOut,
    }),
    [status, session, user, memberships, studios, activeStudioId, setActiveStudioId, refreshMemberships, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
